import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isCouponItem } from '@hooks/useShoppingCart/coupon';
import { fetchEquipment } from '@services/productOfferingService';
import { selectParsedEquipment } from '@utils/equipmentMapper';
import type { ParsedEquipment } from '@t/equipmentOffering.types';
import type { ShoppingCartDTO } from '@t/api.types';

/**
 * Declaratively fetches equipment for every plan in the cart.
 * Fires as soon as the cart loads — no popup interaction required.
 * Returns a stable map of planId → ParsedEquipment[].
 */
export const useCartEquipment = (sessionId: string, homeId: string, cart: ShoppingCartDTO | null) => {
  const planIds = useMemo(
    () =>
      (cart?.cartItem ?? [])
        .filter((ci) => !isCouponItem(ci) && ci.type !== 'ev')
        .map((ci) => ci.productOffering?.id)
        .filter(Boolean) as string[],
    [cart]
  );

  const queries = useQueries({
    queries: planIds.map((planId) => ({
      queryKey: ['equipment', sessionId, homeId, planId] as const,
      queryFn: () => fetchEquipment(sessionId, homeId, planId),
      select: selectParsedEquipment,
      enabled: !!sessionId && !!homeId && !!planId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
  });

  const equipmentByPlanId = useMemo(() => {
    const map = new Map<string, ParsedEquipment[]>();
    planIds.forEach((planId, i) => {
      const data = queries[i]?.data;
      if (data) map.set(planId, data);
    });
    return map;
  }, [planIds, queries]);

  return equipmentByPlanId;
};
